import InfoOutlined from '@mui/icons-material/InfoOutlined'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { EligibilityAreaItem } from './EligibilityAreaItem'
import { MonitoringAccordion } from '../MonitoringAccordion'
import { useEligibilityCheckContext } from './EligibilityCheckContext'
import { useProcessLoading } from '../../../utils/Loading/useProcessLoading'

export const EligibilityCheckAccordion = () => {
  const {
    isLoadingEligibilityChecks,
    eligibilityCheckAreas: items,
    setSelectedEligibilityAreaIds,
    isEligibilityVisible,
    setEligibilityVisible,
    createEligibilityCheck,
    isRunningEligibility,
    startEligibilityCheck,
    clearEligibilityCheck,
  } = useEligibilityCheckContext()

  const { processLoading } = useProcessLoading()

  const [accordionExpanded, setAccordionExpanded] = useState(true)
  // In-flight guard for the window between the click and the blocking modal, which mounts on the
  // next render; the persisted flag only rises once doAction runs.
  const [isStarting, setIsStarting] = useState(false)
  const isCheckBlocked = isRunningEligibility || isStarting

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEligibilityVisible(prev => !prev)
  }

  const handleEligibilityCheck = async () => {
    setIsStarting(true)
    try {
      await processLoading({
        loadingMessage: 'Realizando chequeo de elegibilidad',
        successfulMessage:
          'Chequeo de elegibilidad iniciado exitosamente. Recibirá un correo con el resultado en breve.',
        errorMessage: 'Error al iniciar el chequeo de elegibilidad',
        // The persisted flag is raised before the request leaves the browser: raising it after the
        // response would lose the lock whenever the response never arrives, letting a reload
        // enqueue a second detection for a job the backend already accepted.
        doAction: async () => {
          startEligibilityCheck()
          try {
            await createEligibilityCheck()
          } catch (error) {
            clearEligibilityCheck()
            throw error
          }
        },
      })
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <MonitoringAccordion
      enableSelection
      expanded={isEligibilityVisible && accordionExpanded}
      isLoading={isLoadingEligibilityChecks}
      items={items}
      onChange={(_, expanded) => setAccordionExpanded(expanded)}
      onItemsChange={newItems =>
        setSelectedEligibilityAreaIds(
          newItems.filter(item => item.properties.selected).map(item => item.properties.id),
        )
      }
    >
      <MonitoringAccordion.Title enableSelection>
        <Stack direction="row" spacing={1}>
          <span>Chequeo de elegibilidad</span>
          <Tooltip
            placement="top"
            title="Aquí se puede observar la detección automática de áreas no elegibles para el programa de carbono."
          >
            <Box component="span" sx={{ display: 'flex' }} onClick={e => e.stopPropagation()}>
              <InfoOutlined />
            </Box>
          </Tooltip>
        </Stack>
      </MonitoringAccordion.Title>
      <MonitoringAccordion.Description sx={{ flexGrow: 1 }} textAlign="right">
        <IconButton
          component="span"
          disabled={isLoadingEligibilityChecks}
          onClick={handleToggleVisibility}
        >
          {isEligibilityVisible ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </MonitoringAccordion.Description>
      <MonitoringAccordion.Item>
        <Typography variant="body2">
          Estas areas se asumen como “con pastoreo”. De existir dentro de ellas, areas no
          pastoreadas, es necesario que la dibuje como un <b>area de exclusión “sin pastoreo”</b>.
        </Typography>
      </MonitoringAccordion.Item>
      {items?.map(item => (
        <MonitoringAccordion.Item key={item.properties.id}>
          <EligibilityAreaItem area={item} />
        </MonitoringAccordion.Item>
      ))}
      {(!items || items.length === 0) && (
        <MonitoringAccordion.Item sx={{ mt: 2 }}>
          <Tooltip
            placement="top"
            title={isCheckBlocked ? 'El proceso de chequeo está siendo realizado' : ''}
          >
            <span style={{ width: '100%' }}>
              <Button
                disabled={isCheckBlocked}
                sx={{ width: '100%' }}
                variant="outlined"
                onClick={handleEligibilityCheck}
              >
                Realizar chequeo de elegibilidad
              </Button>
            </span>
          </Tooltip>
        </MonitoringAccordion.Item>
      )}
    </MonitoringAccordion>
  )
}
